import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Heading, Pane, Paragraph, ProjectsIcon, SideSheet, Tab, TabNavigation } from "evergreen-ui";
import { CostMetadata, CostPayoutModifier, ProductListingToPayoutRule } from "../../../../common/models";
import { CostMetadataTableRow } from "../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { PLPRTableRow } from "../../../../common/tables/product-listing/payout-rule-mapping/PLPRTableRow";
import { QueryInput } from "../../../../common/query-filters";
import { AbstractTableViewProvider } from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { CellType } from "../../common/tables/config/CellType";
import { ColumnConfig } from "../../common/tables/config/ColumnConfig";
import { DefaultCostMetadataTableConfig } from "../MultiTabProductPage/cost-metadata/tables/config/DefaultCostMetadataTableConfig";
import { CostMetadataTableCellFactory } from "../MultiTabProductPage/cost-metadata/factory/CostMetadataTableCellFactory";
import { DefaultPLPRTableConfig } from "../MultiTabProductPage/payout-rule-mapping/tables/config/DefaultPLPRTableConfig";
import { PLPRTableCellFactory } from "../MultiTabProductPage/payout-rule-mapping/factory/PLPRTableCellFactory";
import { queryCostPayoutModifierById } from "../../datastore/CostPayoutModifierDataStore";
import { TableCellFactory } from "../../common/tables/factory";

function toReadOnly<T>(col: ColumnConfig<T>): ColumnConfig<T> {
    switch (col.cellType) {
        case CellType.textInput:
        case CellType.textInputWithMenuButton:
        case CellType.singleSelect:
        case CellType.numberInput:
            return { ...col, cellType: CellType.readOnlyText };
        case CellType.datePicker:
        case CellType.dateTimePicker:
            return { ...col, cellType: CellType.readOnlyDate };
        default:
            return col;
    }
}

export type CostPayoutModifierWithRelations = CostPayoutModifier & {
    costMetadata?: CostMetadata[];
    payoutRuleMappings?: ProductListingToPayoutRule[];
};

const TAB_LABELS = ["Cost Metadata", "Payout Rule Mappings"];

interface CPMDetailSideSheetProps {
    isShown: boolean;
    costPayoutModifier: CostPayoutModifier | null;
    initialTabIndex: number;
    onDismiss: () => void;
}

export const CPMDetailSideSheet: FC<CPMDetailSideSheetProps> = ({
    isShown,
    costPayoutModifier,
    initialTabIndex,
    onDismiss,
}) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(initialTabIndex);
    const [cpmDetail, setCpmDetail] = useState<CostPayoutModifierWithRelations | null>(null);

    useEffect(() => {
        setSelectedTabIndex(initialTabIndex);
    }, [initialTabIndex]);

    const cpmId = costPayoutModifier?.id;

    useEffect(() => {
        if (isShown && cpmId) {
            queryCostPayoutModifierById(cpmId)
                .then((result) => {
                    setCpmDetail(result as CostPayoutModifierWithRelations);
                })
                .catch(() => {
                    setCpmDetail(null);
                });
        }

        if (!isShown) {
            setCpmDetail(null);
        }
    }, [isShown, cpmId]);

    if (!costPayoutModifier) {
        return null;
    }

    return (
        <SideSheet
            width={"75vw"}
            isShown={isShown}
            onCloseComplete={onDismiss}
        >
            <Pane padding={24}>
                <CPMHeaderSummary costPayoutModifier={costPayoutModifier} />
                <TabNavigation marginBottom={16}>
                    {TAB_LABELS.map((tab, index) => (
                        <Tab
                            key={tab}
                            isSelected={selectedTabIndex === index}
                            onSelect={() => setSelectedTabIndex(index)}
                        >
                            {tab}
                        </Tab>
                    ))}
                </TabNavigation>
                <Pane>
                    {selectedTabIndex === 0 && cpmDetail && (
                        <CostMetadataTabContent
                            costMetadata={cpmDetail.costMetadata ?? []}
                            cpmName={costPayoutModifier.name}
                        />
                    )}
                    {selectedTabIndex === 1 && cpmDetail && (
                        <PLPRTabContent
                            payoutRuleMappings={cpmDetail.payoutRuleMappings ?? []}
                            cpmName={costPayoutModifier.name}
                        />
                    )}
                </Pane>
            </Pane>
        </SideSheet>
    );
};

const CPMHeaderSummary: FC<{ costPayoutModifier: CostPayoutModifier }> = ({ costPayoutModifier }) => {
    const conditions: string[] = [];

    if (costPayoutModifier.conditionCountryCode) {
        conditions.push(`Country: ${costPayoutModifier.conditionCountryCode}`);
    }
    if (costPayoutModifier.conditionEmailDomains && costPayoutModifier.conditionEmailDomains.length > 0) {
        conditions.push(`Email Domains: ${costPayoutModifier.conditionEmailDomains.join(", ")}`);
    }
    if (costPayoutModifier.conditionIsBundle !== undefined && costPayoutModifier.conditionIsBundle !== null) {
        conditions.push(`Bundle: ${costPayoutModifier.conditionIsBundle ? "Yes" : "No"}`);
    }
    if (costPayoutModifier.conditionSourceName) {
        conditions.push(`Source: ${costPayoutModifier.conditionSourceName}`);
    }
    if (costPayoutModifier.conditionFulfillmentService) {
        conditions.push(`Fulfillment: ${costPayoutModifier.conditionFulfillmentService}`);
    }
    conditions.push(`Priority: ${costPayoutModifier.priority}`);

    return (
        <Pane marginBottom={20}>
            <Heading size={600} marginBottom={8}>{costPayoutModifier.name}</Heading>
            <Paragraph size={300} color={"muted"}>
                {conditions.join(" · ")}
            </Paragraph>
        </Pane>
    );
};

const CostMetadataTabContent: FC<{ costMetadata: CostMetadata[]; cpmName: string }> = ({
    costMetadata,
    cpmName,
}) => {
    const costMetadataTableCellFactory: TableCellFactory<CostMetadataTableRow, CostMetadata> = useMemo(() => {
        return new CostMetadataTableCellFactory();
    }, []);

    const queryTableData = useCallback((_input: QueryInput<CostMetadataTableRow>) => {
        return Promise.resolve(costMetadata);
    }, [costMetadata]);

    const onUpdateRow = useCallback((
        costMetadataList: CostMetadata[],
    ): CostMetadata[] => {
        return costMetadataList;
    }, []);

    const costMetadataConfig = useMemo(() => {
        return {
            ...DefaultCostMetadataTableConfig,
            columns: DefaultCostMetadataTableConfig.columns.map(col => {
                if (col.columnName === "costPayoutModifierName") {
                    return { ...col, hidden: true };
                }
                return toReadOnly(col);
            }),
        };
    }, []);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No cost metadata linked to this modifier",
            secondaryText: "",
            icon: <Pane marginTop={80}><ProjectsIcon size={60} /></Pane>,
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load cost metadata",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    return (
        <AbstractTableViewProvider
            queryTableData={queryTableData}
            onUpdateRow={onUpdateRow}
            updateRowLoading={false}
            updateRowCalled={false}
            updateRowError={false}
            createRowCalled={false}
            createRowError={undefined}
            createRowLoading={false}
            tableCellFactory={costMetadataTableCellFactory}
            savedTableConfigProps={{
                tableName: "cpmDetailCostMetadata",
                identifiers: [cpmName],
                defaultConfigValue: costMetadataConfig,
            }}
        >
            <TableView
                errorViewProps={errorViewProps}
                emptyViewProps={emptyViewProps}
            />
        </AbstractTableViewProvider>
    );
};

const PLPRTabContent: FC<{ payoutRuleMappings: ProductListingToPayoutRule[]; cpmName: string }> = ({
    payoutRuleMappings,
    cpmName,
}) => {
    const plprTableCellFactory: PLPRTableCellFactory = useMemo(() => {
        return new PLPRTableCellFactory();
    }, []);

    const queryTableData = useCallback((_input: QueryInput<PLPRTableRow>) => {
        return Promise.resolve(payoutRuleMappings);
    }, [payoutRuleMappings]);

    const onUpdateRow = useCallback((
        plprList: ProductListingToPayoutRule[],
    ): ProductListingToPayoutRule[] => {
        return plprList;
    }, []);

    const plprConfig = useMemo(() => {
        return {
            ...DefaultPLPRTableConfig,
            columns: DefaultPLPRTableConfig.columns.map(col => {
                if (col.columnName === "costPayoutModifierId") {
                    return { ...col, hidden: true };
                }
                return toReadOnly(col);
            }),
        };
    }, []);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No payout rule mappings linked to this modifier",
            secondaryText: "",
            icon: <Pane marginTop={80}><ProjectsIcon size={60} /></Pane>,
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load payout rule mappings",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    return (
        <>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={false}
                updateRowCalled={false}
                updateRowError={false}
                createRowCalled={false}
                createRowError={undefined}
                createRowLoading={false}
                tableCellFactory={plprTableCellFactory}
                savedTableConfigProps={{
                    tableName: "cpmDetailPLPR",
                    identifiers: [cpmName],
                    defaultConfigValue: plprConfig,
                }}
            >
                <TableView
                    errorViewProps={errorViewProps}
                    emptyViewProps={emptyViewProps}
                />
            </AbstractTableViewProvider>
        </>
    );
};
